import { useState, useEffect, useRef } from "react";
import { useIsMobile } from "../hooks/use-mobile";
import LeftSidebar from "../pages/LeftSidebar";
import MobileHeader from "../pages/MobileHeader";
import BottomNavbar from "./home_components/BottomNavBar";
import MobileSidebar from "./home_components/MobileSidebar";
import EventForm from "../components/event_components/EventForm";

function MainLayout({
  children,
  rightSidebar,
  rs = true,
  onMenuClick,
  menu = false,
  pad = true,
  isMobilePadding = true
}) {
  const [showForm, setShowForm] = useState(false);
  const [viewportHeight, setViewportHeight] = useState("100vh");
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const isMobile = useIsMobile();

  const mobileHeaderRef = useRef(null);
  const bottomNavbarRef = useRef(null);
  const [availableMobileHeight, setAvailableMobileHeight] = useState("auto");

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(`${window.innerHeight}px`);
    };

    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);

    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  useEffect(
    () => {
      if (!isMobile) return;

      const handleScroll = () => {
        const currentScrollPos = window.pageYOffset;
        const visible =
          prevScrollPos > currentScrollPos || currentScrollPos < 10;
        setPrevScrollPos(currentScrollPos);
        setIsNavbarVisible(visible);
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    },
    [prevScrollPos, isMobile]
  );

  useEffect(
    () => {
      if (isMobile) {
        const calculateAvailableHeight = () => {
          const headerHeight = mobileHeaderRef.current
            ? mobileHeaderRef.current.offsetHeight
            : 0;
          const navbarHeight = bottomNavbarRef.current
            ? bottomNavbarRef.current.offsetHeight
            : 0;
          const totalHeight = window.innerHeight;
          setAvailableMobileHeight(
            `${totalHeight - headerHeight - navbarHeight}px`
          );
        };

        calculateAvailableHeight();
        window.addEventListener("resize", calculateAvailableHeight);

        return () =>
          window.removeEventListener("resize", calculateAvailableHeight);
      }
    },
    [isMobile]
  );

  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50 font-afacad flex flex-col">
        <MobileHeader
          onMenuClick={onMenuClick}
          menu={menu}
          ref={mobileHeaderRef}
        />

        {/* Mobile Main Content Area */}
        <main
          className={`flex-grow overflow-y-auto ${
            isMobilePadding ? "pb-20" : ""
          }`}
          style={{ height: availableMobileHeight }} // This applies the calculated height
        >
          {children}
        </main>

        <BottomNavbar isNavbarVisible={isNavbarVisible} ref={bottomNavbarRef} />

        {/* Mobile Sidebar */}
        <MobileSidebar
          isMobileSidebarOpen={isMobileSidebarOpen}
          onClose={() => setIsMobileSidebarOpen(false)}
        />
      </div>
    );
  }

  // Desktop layout (unchanged)
  return (
    <div className="flex justify-center w-full bg-pink-50 font-afacad">
      <div className="grid grid-cols-12 w-full max-w-[1600px] bg-white">
        <LeftSidebar viewportHeight={viewportHeight} />
        <main
          className={`${
            rs
              ? "col-span-6 xl:col-span-6"
              : "col-span-8 lg:col-span-10 xl:col-span-10"
          } overflow-y-auto border-gray-200`}
        >
          <div className={`max-w-full ${pad ? "p-4 lg:p-6" : ""}`}>
            {children}
          </div>
        </main>
        {rs && rightSidebar ? (
          <aside className="col-span-4 xl:col-span-4 hidden lg:block border-l-2 border-gray-100 overflow-y-auto h-fit">
            {rightSidebar}
          </aside>
        ) : null}
      </div>
      {showForm && <EventForm onClose={() => setShowForm(false)} />}
    </div>
  );
}

export default MainLayout;
